import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { SearchBar } from './SearchBar';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { UniversalUpload } from './UniversalUpload';
import { 
  Plus, 
  Edit, 
  Users, 
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Calendar,
  FileText,
  Trash2,
  AlertTriangle,
  Camera
} from 'lucide-react';
import { apiService } from '../../services/api';

export const ClientesPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = user?.tipo === 'sub_admin';

  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState('');

  const qCli = busca.trim().toLowerCase();
  const qCliDigits = qCli.replace(/\D/g, '');
  const clientesFiltrados = qCli ? clientes.filter(c =>
    (c.nome || '').toLowerCase().includes(qCli) ||
    (qCliDigits && String(c.cpf || '').replace(/\D/g, '').includes(qCliDigits)) ||
    (qCliDigits && String(c.telefone || '').replace(/\D/g, '').includes(qCliDigits)) ||
    (c.email || '').toLowerCase().includes(qCli)
  ) : clientes;
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [clienteDuplicado, setClienteDuplicado] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showBloqueioDialog, setShowBloqueioDialog] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [motivoBloqueio, setMotivoBloqueio] = useState('');
  const [observacoesBloqueio, setObservacoesBloqueio] = useState('');
  
  // Estados para cadastro com IA
  const [showIAModal, setShowIAModal] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [documents, setDocuments] = useState({
    cnh: null,
    contrato: null,
    comprovante: null
  });
  
  // Estados para versão rápida (só contrato)
  const [showQuickIAModal, setShowQuickIAModal] = useState(false);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    telefone: '',
    email: '',
    cnh: '',
    data_nascimento: '',
    endereco: '',
    cidade: 'Praia Grande',
    cep: '',
    profissao: '',
    estado_civil: '',
    observacoes: ''
  });
  const [documentos, setDocumentos] = useState([]);

  useEffect(() => {
    carregarClientes();
  }, []);

  const carregarClientes = async () => {
    try {
      const data = await apiService.getClientes();
      setClientes(data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCliente) {
        await apiService.atualizarCliente(editingCliente.id, formData);
        setShowModal(false);
        setEditingCliente(null);
        resetForm();
        carregarClientes();
      } else {
        await apiService.criarCliente(formData);
        setShowModal(false);
        setEditingCliente(null);
        resetForm();
        carregarClientes();
      }
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      
      // Verificar se é erro de cliente duplicado
      if (error.response?.status === 409) {
        const errorData = error.response.data.detail;
        if (errorData.cliente_existente) {
          setClienteDuplicado(errorData.cliente_existente);
          setShowDuplicateDialog(true);
          return;
        }
      }
      
      alert('Erro ao salvar cliente. Verifique os dados e tente novamente.');
    }
  };

  // Função para extrair dados com IA
  const handleExtractWithIA = async () => {
    if (!documents.cnh || !documents.contrato || !documents.comprovante) {
      alert('Por favor, faça upload dos 3 documentos (CNH, Contrato e Comprovante)');
      return;
    }

    setUploadingDocs(true);
    try {
      const formData = new FormData();
      formData.append('cnh', documents.cnh);
      formData.append('contrato', documents.contrato);
      formData.append('comprovante', documents.comprovante);

      const response = await apiService.extractClienteFromDocuments(formData);
      
      if (response.success) {
        // Mesclar dados extraídos com URLs dos documentos
        const dataWithDocs = {
          ...response.cliente_data,
          documento_cnh_url: response.documentos.cnh_url,
          documento_contrato_url: response.documentos.contrato_url,
          documento_comprovante_url: response.documentos.comprovante_url
        };
        setExtractedData(dataWithDocs);
        alert('✅ Dados extraídos com sucesso! Revise antes de salvar.');
      } else {
        alert('Erro ao extrair dados: ' + response.error);
      }
    } catch (error) {
      console.error('Erro ao extrair dados:', error);
      alert('Erro ao processar documentos. Tente novamente ou cadastre manualmente.');
    } finally {
      setUploadingDocs(false);
    }
  };

  // Função para salvar cliente a partir dos dados extraídos
  const handleSaveExtractedCliente = async () => {
    if (!extractedData) return;

    try {
      // Formatar dados para o formato esperado pela API
      const clienteData = {
        nome: extractedData.nome,
        cpf: extractedData.cpf?.replace(/\D/g, ''),
        rg: extractedData.rg,
        telefone: extractedData.telefone,
        email: extractedData.email || '',
        cnh: extractedData.numero_cnh,
        categoria_cnh: extractedData.categoria_cnh,
        validade_cnh: extractedData.validade_cnh,
        data_nascimento: extractedData.data_nascimento,
        endereco: extractedData.endereco,
        bairro: extractedData.bairro,
        cidade: extractedData.cidade,
        estado: extractedData.estado || 'SP',
        cep: extractedData.cep?.replace(/\D/g, ''),
        profissao: extractedData.profissao || '',
        estado_civil: extractedData.estado_civil || '',
        nome_pai: extractedData.nome_pai,
        nome_mae: extractedData.nome_mae,
        observacoes: extractedData.plano ? `Plano: ${extractedData.plano}` : '',
        // URLs dos documentos
        documento_cnh_url: extractedData.documento_cnh_url,
        documento_contrato_url: extractedData.documento_contrato_url,
        documento_comprovante_url: extractedData.documento_comprovante_url
      };

      await apiService.criarCliente(clienteData);
      
      // Limpar e fechar
      setShowIAModal(false);
      setExtractedData(null);
      setDocuments({ cnh: null, contrato: null, comprovante: null });
      carregarClientes();
      alert('✅ Cliente cadastrado com sucesso!');
      
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      
      if (error.response?.status === 409) {
        alert('Cliente já cadastrado com este CPF!');
      } else {
        alert('Erro ao salvar cliente. Verifique os dados e tente novamente.');
      }
    }
  };

  // Função para extração rápida (só contrato)
  const handleQuickExtract = async () => {
    if (!contractFile) {
      alert('Por favor, faça upload do contrato');
      return;
    }

    setUploadingContract(true);
    try {
      const formData = new FormData();
      formData.append('contrato', contractFile);

      const response = await apiService.extractClienteFromContractOnly(formData);
      
      if (response.success) {
        const dataWithDocs = {
          ...response.cliente_data,
          documento_contrato_url: response.documentos.contrato_url
        };
        setExtractedData(dataWithDocs);
        alert('✅ Dados extraídos! Complete CNH e data de nascimento antes de salvar.');
      } else {
        alert('Erro ao extrair dados: ' + response.error);
      }
    } catch (error) {
      console.error('Erro ao extrair dados:', error);
      alert('Erro ao processar contrato. Tente novamente ou cadastre manualmente.');
    } finally {
      setUploadingContract(false);
    }
  };

  const irParaClienteExistente = () => {
    setShowDuplicateDialog(false);
    setShowModal(false);
    setClienteDuplicado(null);
    resetForm();
    // Destacar cliente existente (pode implementar scroll ou destaque visual)
    alert(`Cliente já cadastrado: ${clienteDuplicado.nome}`);
  };

  const continuarCadastro = () => {
    setShowDuplicateDialog(false);
    setClienteDuplicado(null);
  };

  const abrirDialogoBloqueio = (cliente) => {
    setClienteSelecionado(cliente);
    setMotivoBloqueio('');
    setObservacoesBloqueio('');
    setShowBloqueioDialog(true);
  };

  const bloquearCliente = async () => {
    if (!motivoBloqueio.trim()) {
      alert('Motivo do bloqueio é obrigatório');
      return;
    }

    try {
      await apiService.bloquearCliente(clienteSelecionado.id, {
        motivo: motivoBloqueio,
        observacoes: observacoesBloqueio
      });
      
      setShowBloqueioDialog(false);
      setMotivoBloqueio('');
      setObservacoesBloqueio('');
      carregarClientes();
      alert('Cliente bloqueado com sucesso!');
    } catch (error) {
      console.error('Erro ao bloquear cliente:', error);
      alert('Erro ao bloquear cliente. Tente novamente.');
    }
  };

  const desbloquearCliente = async (cliente) => {
    const motivo = prompt('Motivo do desbloqueio:');
    if (!motivo) return;

    try {
      await apiService.desbloquearCliente(cliente.id, {
        motivo: motivo,
        observacoes: 'Desbloqueio via painel administrativo'
      });
      
      carregarClientes();
      alert('Cliente desbloqueado com sucesso!');
    } catch (error) {
      console.error('Erro ao desbloquear cliente:', error);
      alert('Erro ao desbloquear cliente. Tente novamente.');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      cpf: '',
      telefone: '',
      email: '',
      cnh: '',
      data_nascimento: '',
      endereco: '',
      cidade: 'Praia Grande',
      cep: '',
      profissao: '',
      estado_civil: '',
      observacoes: ''
    });
    setDocumentos([]);
  };

  const handleDocumentAdd = (newDocuments) => {
    setDocumentos(prev => [...prev, ...newDocuments]);
  };

  const criarAcessoApp = async (cliente) => {
    const isReset = !!cliente.app_senha_hash;
    const acao = isReset ? 'Resetar a senha do app' : 'Criar acesso ao app';
    if (!window.confirm(`${acao} para ${cliente.nome}?`)) return;

    // Permite admin definir senha personalizada (importante caso WhatsApp falhe)
    const senha_customizada = window.prompt(
      'Digite uma senha personalizada (mín. 4 caracteres) OU deixe vazio pra gerar aleatória:',
      ''
    );
    if (senha_customizada === null) return; // cancelou

    try {
      const endpoint = isReset
        ? `/admin/clientes/${cliente.id}/resetar-senha-app`
        : `/admin/clientes/${cliente.id}/criar-acesso-app`;
      const body = senha_customizada.trim().length >= 4
        ? { senha_customizada: senha_customizada.trim() }
        : {};
      const { data } = await apiService.api.post(endpoint, body);
      const senha = data.senha_inicial;
      const placa = data.placa || '<a placa da sua moto>';
      const url = `${window.location.origin}/cliente`;
      const primeiroNome = (cliente.nome || '').split(' ')[0] || 'Cliente';
      const acaoMsg = isReset ? 'sua senha foi *resetada*' : 'seu acesso ao app *Desloca* foi criado';

      const mensagem =
        `🏍️ *Olá, ${primeiroNome}!*\n\n` +
        `${acaoMsg}. Acompanhe pagamentos, débitos, comprovantes e o status da sua moto direto pelo celular.\n\n` +
        `🔗 ${url}\n\n` +
        `🔐 *Seus dados de acesso:*\n` +
        `👤 CPF: ${cliente.cpf}\n` +
        `🏍️ Placa: ${placa}\n` +
        `🔑 Senha provisória: ${senha}\n\n` +
        `⚠️ No primeiro acesso, você criará sua senha pessoal.\n\n` +
        `📱 *Como instalar no celular:*\n` +
        `• iPhone (Safari): toque em "Compartilhar" → "Adicionar à Tela de Início"\n` +
        `• Android (Chrome): toque no menu (3 pontinhos) → "Instalar app"`;

      const telLimpo = String(cliente.telefone || '').replace(/\D/g, '');
      const telE164 = telLimpo.startsWith('55') ? telLimpo : `55${telLimpo}`;

      // Copia a mensagem para a área de transferência ANTES de abrir WhatsApp
      try { await navigator.clipboard.writeText(mensagem); } catch {/* sem permissão */}

      if (telLimpo.length >= 10) {
        const waUrl = `https://wa.me/${telE164}?text=${encodeURIComponent(mensagem)}`;
        alert(`✅ ${isReset ? 'Senha resetada' : 'Acesso criado'}!\n\nSenha: ${senha}\n\nA mensagem foi copiada — vou abrir o WhatsApp agora. Se não abrir automaticamente, cole a mensagem manualmente.`);
        // location.href funciona em iOS Safari mesmo após await — window.open não funciona
        window.location.href = waUrl;
      } else {
        alert(`✅ ${isReset ? 'Senha resetada' : 'Acesso criado'}!\n\n⚠️ Cliente sem telefone — mensagem copiada pra área de transferência.\nSenha: ${senha}`);
      }
      carregarClientes();
    } catch (e) {
      alert('Erro ao criar acesso: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nome: cliente.nome,
      cpf: cliente.cpf,
      telefone: cliente.telefone,
      email: cliente.email || '',
      cnh: cliente.cnh,
      data_nascimento: cliente.data_nascimento,
      endereco: cliente.endereco,
      cidade: cliente.cidade,
      cep: cliente.cep,
      profissao: cliente.profissao || '',
      estado_civil: cliente.estado_civil || '',
      observacoes: cliente.observacoes || ''
    });
    setDocumentos(cliente.documentos || []);
    setShowModal(true);
  };

  const handleDelete = async (cliente) => {
    if (window.confirm(`Tem certeza que deseja excluir o cliente ${cliente.nome}?`)) {
      try {
        await apiService.deletarCliente(cliente.id);
        carregarClientes();
        alert('Cliente excluído com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir cliente:', error);
        alert('Erro ao excluir cliente. Verifique se não há locações ativas.');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativo': return 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30';
      case 'inativo': return 'bg-slate-700/50 text-gray-300 border border-white/10';
      case 'bloqueado': return 'bg-red-500/20 text-red-300 border border-red-500/30';
      default: return 'bg-slate-700/50 text-gray-300 border border-white/10';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ativo': return 'Ativo';
      case 'inativo': return 'Inativo';
      case 'bloqueado': return 'Bloqueado';
      default: return status;
    }
  };

  const formatCPF = (cpf) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone) => {
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  if (loading) {
    return <div className="flex justify-center p-8">Carregando clientes...</div>;
  }

  return (
    <div className="admin-page space-y-6 min-h-screen pb-20">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Gestão de Clientes</h2>
        
        <div className="flex gap-2">
          {/* Botão Cadastro RÁPIDO com IA (SÓ CONTRATO) */}
          <Dialog open={showQuickIAModal} onOpenChange={setShowQuickIAModal}>
            <DialogTrigger asChild>
              <Button 
                className="bg-green-600 hover:bg-green-700" 
                onClick={() => {
                  setExtractedData(null);
                  setContractFile(null);
                }}
              >
                <Camera className="h-4 w-4 mr-2" />
                Cadastro Rápido ⚡
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl">
                  ⚡ Cadastro Rápido com IA (Recomendado!)
                </DialogTitle>
                <p className="text-sm text-gray-400 mt-2">
                  Upload apenas do <strong>CONTRATO</strong> e a IA extrai a maioria dos dados!
                </p>
              </DialogHeader>
              
              {!extractedData ? (
                // Upload do contrato
                <div className="space-y-6">
                  <Alert className="bg-emerald-500/10 border-emerald-500/30">
                    <AlertDescription className="text-emerald-300">
                      <div className="space-y-2">
                        <p className="font-semibold">✨ Por que é mais rápido?</p>
                        <ul className="list-disc ml-5 space-y-1 text-sm text-gray-300">
                          <li>Upload de <strong className="text-white">apenas 1 documento</strong> (contrato)</li>
                          <li>IA extrai: nome, CPF, RG, telefone, email, endereço</li>
                          <li>Você adiciona CNH e data de nascimento manualmente</li>
                          <li>Tempo: ~15 segundos (3x mais rápido!)</li>
                        </ul>
                      </div>
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label>Contrato de Locação Assinado</Label>
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setContractFile(e.target.files[0])}
                      className="cursor-pointer"
                    />
                    {contractFile && (
                      <p className="text-sm text-emerald-400">✅ {contractFile.name}</p>
                    )}
                  </div>

                  <Button
                    onClick={handleQuickExtract}
                    disabled={!contractFile || uploadingContract}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {uploadingContract ? (
                      <>⏳ Processando...</>
                    ) : (
                      <>⚡ Extrair Dados Rápido</>
                    )}
                  </Button>
                </div>
              ) : (
                // Revisão dos dados
                <div className="space-y-6">
                  <Alert className="bg-emerald-500/10 border-emerald-500/30">
                    <AlertDescription className="text-emerald-300">
                      ✅ Dados extraídos! Complete CNH e data de nascimento antes de salvar.
                    </AlertDescription>
                  </Alert>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Nome Completo *</Label>
                      <Input
                        value={extractedData.nome || ''}
                        onChange={(e) => setExtractedData({...extractedData, nome: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>CPF *</Label>
                      <Input
                        value={extractedData.cpf || ''}
                        onChange={(e) => setExtractedData({...extractedData, cpf: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>RG</Label>
                      <Input
                        value={extractedData.rg || ''}
                        onChange={(e) => setExtractedData({...extractedData, rg: e.target.value})}
                      />
                    </div>

                    <div className="col-span-2 bg-amber-500/10 p-3 rounded border border-amber-500/30">
                      <p className="text-sm font-semibold text-amber-300 mb-2">
                        ✏️ Preencha manualmente (não estava no contrato):
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Número da CNH *</Label>
                          <Input
                            placeholder="Ex: 12345678900"
                            value={extractedData.numero_cnh || ''}
                            onChange={(e) => setExtractedData({...extractedData, numero_cnh: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Data de Nascimento *</Label>
                          <Input
                            type="date"
                            value={extractedData.data_nascimento || ''}
                            onChange={(e) => setExtractedData({...extractedData, data_nascimento: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Categoria CNH</Label>
                          <Input
                            placeholder="Ex: AB"
                            value={extractedData.categoria_cnh || ''}
                            onChange={(e) => setExtractedData({...extractedData, categoria_cnh: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Validade CNH</Label>
                          <Input
                            type="date"
                            value={extractedData.validade_cnh || ''}
                            onChange={(e) => setExtractedData({...extractedData, validade_cnh: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label>Telefone *</Label>
                      <Input
                        value={extractedData.telefone || ''}
                        onChange={(e) => setExtractedData({...extractedData, telefone: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Email</Label>
                      <Input
                        value={extractedData.email || ''}
                        onChange={(e) => setExtractedData({...extractedData, email: e.target.value})}
                      />
                    </div>

                    <div className="col-span-2">
                      <Label>Endereço *</Label>
                      <Input
                        value={extractedData.endereco || ''}
                        onChange={(e) => setExtractedData({...extractedData, endereco: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Bairro</Label>
                      <Input
                        value={extractedData.bairro || ''}
                        onChange={(e) => setExtractedData({...extractedData, bairro: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Cidade</Label>
                      <Input
                        value={extractedData.cidade || ''}
                        onChange={(e) => setExtractedData({...extractedData, cidade: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>CEP</Label>
                      <Input
                        value={extractedData.cep || ''}
                        onChange={(e) => setExtractedData({...extractedData, cep: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Profissão</Label>
                      <Input
                        value={extractedData.profissao || ''}
                        onChange={(e) => setExtractedData({...extractedData, profissao: e.target.value})}
                        placeholder="Ex: Motoboy, Vendedor, Autônomo"
                      />
                    </div>

                    <div>
                      <Label>Estado Civil</Label>
                      <select
                        value={extractedData.estado_civil || ''}
                        onChange={(e) => setExtractedData({...extractedData, estado_civil: e.target.value})}
                        className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Selecione</option>
                        <option value="solteiro">Solteiro(a)</option>
                        <option value="casado">Casado(a)</option>
                        <option value="divorciado">Divorciado(a)</option>
                        <option value="viuvo">Viúvo(a)</option>
                        <option value="uniao_estavel">União Estável</option>
                      </select>
                    </div>

                    <div>
                      <Label>Estado</Label>
                      <Input
                        value={extractedData.estado || 'SP'}
                        onChange={(e) => setExtractedData({...extractedData, estado: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        setExtractedData(null);
                        setShowQuickIAModal(false);
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSaveExtractedCliente}
                      disabled={!extractedData.nome || !extractedData.cpf || !extractedData.numero_cnh || !extractedData.data_nascimento}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      ✅ Salvar Cliente
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Botão Cadastro com IA (COMPLETO - 3 documentos) */}
          <Dialog open={showIAModal} onOpenChange={setShowIAModal}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-600 hover:bg-blue-700" 
                onClick={() => {
                  setExtractedData(null);
                  setDocuments({ cnh: null, contrato: null, comprovante: null });
                }}
              >
                <Camera className="h-4 w-4 mr-2" />
                Cadastro com IA
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl">
                  🤖 Cadastro Automático com Inteligência Artificial
                </DialogTitle>
                <p className="text-sm text-gray-400 mt-2">
                  Faça upload dos 3 documentos e a IA extrairá automaticamente todos os dados!
                </p>
              </DialogHeader>
              
              {!extractedData ? (
                // Etapa 1: Upload dos documentos
                <div className="space-y-6">
                  <Alert className="bg-blue-500/10 border-blue-500/30">
                    <AlertDescription className="text-blue-300">
                      <div className="space-y-2">
                        <p className="font-semibold text-white">📋 Documentos necessários:</p>
                        <ul className="list-disc ml-5 space-y-1 text-gray-300">
                          <li>CNH (Carteira Nacional de Habilitação)</li>
                          <li>Contrato de Locação assinado</li>
                          <li>Comprovante de Residência</li>
                        </ul>
                        <p className="text-sm mt-2">
                          ✨ A IA irá extrair: nome, CPF, RG, endereço, telefone, data de nascimento e muito mais!
                        </p>
                      </div>
                    </AlertDescription>
                  </Alert>

                  {/* Upload CNH */}
                  <div className="space-y-2">
                    <Label>1. CNH (Carteira Nacional de Habilitação)</Label>
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setDocuments({...documents, cnh: e.target.files[0]})}
                      className="cursor-pointer"
                    />
                    {documents.cnh && (
                      <p className="text-sm text-emerald-400">✅ {documents.cnh.name}</p>
                    )}
                  </div>

                  {/* Upload Contrato */}
                  <div className="space-y-2">
                    <Label>2. Contrato de Locação Assinado</Label>
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setDocuments({...documents, contrato: e.target.files[0]})}
                      className="cursor-pointer"
                    />
                    {documents.contrato && (
                      <p className="text-sm text-emerald-400">✅ {documents.contrato.name}</p>
                    )}
                  </div>

                  {/* Upload Comprovante */}
                  <div className="space-y-2">
                    <Label>3. Comprovante de Residência</Label>
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setDocuments({...documents, comprovante: e.target.files[0]})}
                      className="cursor-pointer"
                    />
                    {documents.comprovante && (
                      <p className="text-sm text-emerald-400">✅ {documents.comprovante.name}</p>
                    )}
                  </div>

                  <Button
                    onClick={handleExtractWithIA}
                    disabled={!documents.cnh || !documents.contrato || !documents.comprovante || uploadingDocs}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {uploadingDocs ? (
                      <>⏳ Analisando documentos...</>
                    ) : (
                      <>🤖 Extrair Dados com IA</>
                    )}
                  </Button>
                </div>
              ) : (
                // Etapa 2: Revisão dos dados extraídos
                <div className="space-y-6">
                  <Alert className="bg-emerald-500/10 border-emerald-500/30">
                    <AlertDescription className="text-emerald-300">
                      ✅ Dados extraídos com sucesso! Revise as informações abaixo antes de salvar.
                    </AlertDescription>
                  </Alert>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nome Completo</Label>
                      <Input
                        value={extractedData.nome || ''}
                        onChange={(e) => setExtractedData({...extractedData, nome: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>CPF</Label>
                      <Input
                        value={extractedData.cpf || ''}
                        onChange={(e) => setExtractedData({...extractedData, cpf: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>RG</Label>
                      <Input
                        value={extractedData.rg || ''}
                        onChange={(e) => setExtractedData({...extractedData, rg: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Data de Nascimento</Label>
                      <Input
                        value={extractedData.data_nascimento || ''}
                        onChange={(e) => setExtractedData({...extractedData, data_nascimento: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>CNH</Label>
                      <Input
                        value={extractedData.numero_cnh || ''}
                        onChange={(e) => setExtractedData({...extractedData, numero_cnh: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Categoria CNH</Label>
                      <Input
                        value={extractedData.categoria_cnh || ''}
                        onChange={(e) => setExtractedData({...extractedData, categoria_cnh: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Validade CNH</Label>
                      <Input
                        value={extractedData.validade_cnh || ''}
                        onChange={(e) => setExtractedData({...extractedData, validade_cnh: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Telefone</Label>
                      <Input
                        value={extractedData.telefone || ''}
                        onChange={(e) => setExtractedData({...extractedData, telefone: e.target.value})}
                      />
                    </div>

                    <div className="col-span-2">
                      <Label>Email</Label>
                      <Input
                        value={extractedData.email || ''}
                        onChange={(e) => setExtractedData({...extractedData, email: e.target.value})}
                      />
                    </div>

                    <div className="col-span-2">
                      <Label>Endereço</Label>
                      <Input
                        value={extractedData.endereco || ''}
                        onChange={(e) => setExtractedData({...extractedData, endereco: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Bairro</Label>
                      <Input
                        value={extractedData.bairro || ''}
                        onChange={(e) => setExtractedData({...extractedData, bairro: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Cidade</Label>
                      <Input
                        value={extractedData.cidade || ''}
                        onChange={(e) => setExtractedData({...extractedData, cidade: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>CEP</Label>
                      <Input
                        value={extractedData.cep || ''}
                        onChange={(e) => setExtractedData({...extractedData, cep: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Profissão</Label>
                      <Input
                        value={extractedData.profissao || ''}
                        onChange={(e) => setExtractedData({...extractedData, profissao: e.target.value})}
                        placeholder="Ex: Motoboy, Vendedor, Autônomo"
                      />
                    </div>

                    <div>
                      <Label>Estado Civil</Label>
                      <select
                        value={extractedData.estado_civil || ''}
                        onChange={(e) => setExtractedData({...extractedData, estado_civil: e.target.value})}
                        className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Selecione</option>
                        <option value="solteiro">Solteiro(a)</option>
                        <option value="casado">Casado(a)</option>
                        <option value="divorciado">Divorciado(a)</option>
                        <option value="viuvo">Viúvo(a)</option>
                        <option value="uniao_estavel">União Estável</option>
                      </select>
                    </div>

                    <div>
                      <Label>Estado</Label>
                      <Input
                        value={extractedData.estado || 'SP'}
                        onChange={(e) => setExtractedData({...extractedData, estado: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => setExtractedData(null)}
                      variant="outline"
                      className="flex-1"
                    >
                      ← Voltar
                    </Button>
                    <Button
                      onClick={handleSaveExtractedCliente}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      ✅ Confirmar e Salvar Cliente
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Botão Novo Cliente (manual) */}
          <Dialog open={showModal} onOpenChange={(v) => !isSubAdmin && setShowModal(v)}>
          <DialogTrigger asChild>
            <Button 
              data-testid="btn-novo-cliente"
              disabled={isSubAdmin}
              className={`bg-green-600 hover:bg-green-700 ${isSubAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => {
                if (isSubAdmin) return;
                setEditingCliente(null);
                resetForm();
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingCliente ? 'Editar Cliente' : 'Novo Cliente'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nome">Nome Completo</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    placeholder="Nome completo"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="cpf">CPF</Label>
                  <Input
                    id="cpf"
                    value={formData.cpf}
                    onChange={(e) => setFormData({...formData, cpf: e.target.value.replace(/\D/g, '')})}
                    placeholder="00000000000"
                    maxLength={11}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="telefone">Telefone/WhatsApp</Label>
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => setFormData({...formData, telefone: e.target.value.replace(/\D/g, '')})}
                    placeholder="11999999999"
                    maxLength={11}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">E-mail (opcional)</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="email@exemplo.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cnh">CNH</Label>
                  <Input
                    id="cnh"
                    value={formData.cnh}
                    onChange={(e) => setFormData({...formData, cnh: e.target.value})}
                    placeholder="Número da CNH"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                  <Input
                    id="data_nascimento"
                    type="date"
                    value={formData.data_nascimento}
                    onChange={(e) => setFormData({...formData, data_nascimento: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="endereco">Endereço Completo</Label>
                <Input
                  id="endereco"
                  value={formData.endereco}
                  onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                  placeholder="Rua, número, bairro"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input
                    id="cidade"
                    value={formData.cidade}
                    onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="cep">CEP</Label>
                  <Input
                    id="cep"
                    value={formData.cep}
                    onChange={(e) => setFormData({...formData, cep: e.target.value.replace(/\D/g, '')})}
                    placeholder="00000000"
                    maxLength={8}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="profissao">Profissão</Label>
                  <Input
                    id="profissao"
                    value={formData.profissao}
                    onChange={(e) => setFormData({...formData, profissao: e.target.value})}
                    placeholder="Ex: Motoboy, Vendedor, Autônomo"
                  />
                </div>
                
                <div>
                  <Label htmlFor="estado_civil">Estado Civil</Label>
                  <select
                    id="estado_civil"
                    value={formData.estado_civil}
                    onChange={(e) => setFormData({...formData, estado_civil: e.target.value})}
                    className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione</option>
                    <option value="solteiro">Solteiro(a)</option>
                    <option value="casado">Casado(a)</option>
                    <option value="divorciado">Divorciado(a)</option>
                    <option value="viuvo">Viúvo(a)</option>
                    <option value="uniao_estavel">União Estável</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Observações sobre o cliente..."
                />
              </div>

              {/* Upload de Documentos */}
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-4">Documentos e Fotos</h3>
                {editingCliente?.id ? (
                  <UniversalUpload 
                    entityId={editingCliente.id}
                    entityType="cliente"
                    allowedTypes={['CNH', 'RG', 'Foto', 'Comprovante']}
                    showProfilePicture={true}
                    onProfilePictureSet={(url) => console.log('Foto de perfil definida:', url)}
                  />
                ) : (
                  <div className="p-4 border-2 border-dashed border-white/20 rounded-lg text-center text-gray-400">
                    📁 Documentos poderão ser adicionados após criar o cliente
                  </div>
                )}
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                  {editingCliente ? 'Salvar Alterações' : 'Cadastrar Cliente'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* 🔎 Busca */}
      <SearchBar
        value={busca}
        onChange={setBusca}
        placeholder="Buscar por nome, CPF, telefone ou e-mail..."
        testId="clientes-busca-input"
        resultado={busca ? clientesFiltrados.length : undefined}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {clientesFiltrados.map((cliente) => (
          <Card key={cliente.id}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{cliente.nome}</CardTitle>
                  <p className="text-sm text-gray-400">CPF: {formatCPF(cliente.cpf)}</p>
                </div>
                <div className="flex flex-col space-y-1">
                  <Badge className={getStatusColor(cliente.status)}>
                    {getStatusText(cliente.status)}
                  </Badge>
                  {cliente.bloqueado && (
                    <Badge className="bg-red-500/20 text-red-300 border border-red-500/30">
                      🚫 BLOQUEADO
                    </Badge>
                  )}
                </div>
              </div>
              
              {cliente.bloqueado && (
                <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded">
                  <p className="text-xs text-red-300 font-medium">
                    Motivo: {cliente.motivo_bloqueio}
                  </p>
                  <p className="text-xs text-red-400">
                    Bloqueado em: {new Date(cliente.data_bloqueio).toLocaleDateString('pt-BR')} por {cliente.bloqueado_por}
                  </p>
                </div>
              )}
            </CardHeader>
            
            <CardContent className="space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span>{formatPhone(cliente.telefone)}</span>
                </div>
                
                {cliente.email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="truncate">{cliente.email}</span>
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span>{cliente.cidade}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <CreditCard className="h-4 w-4 text-gray-400" />
                  <span>CNH: {cliente.cnh}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span>{calculateAge(cliente.data_nascimento)} anos</span>
                </div>

                {cliente.documentos && cliente.documentos.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span>{cliente.documentos.length} documento(s)</span>
                  </div>
                )}
              </div>
              
              {cliente.observacoes && (
                <p className="text-xs text-gray-300 bg-slate-700/50 p-2 rounded border border-white/10">
                  {cliente.observacoes}
                </p>
              )}
              
              {/* Documentos Anexados */}
              {(cliente.documento_cnh_url || cliente.documento_contrato_url || cliente.documento_comprovante_url) && (
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-gray-300 mb-2">📄 Documentos:</p>
                  <div className="space-y-1">
                    {cliente.documento_cnh_url && (
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${cliente.documento_cnh_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-xs text-blue-400 hover:text-blue-300 hover:underline"
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        CNH
                      </a>
                    )}
                    {cliente.documento_contrato_url && (
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${cliente.documento_contrato_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-xs text-blue-400 hover:text-blue-300 hover:underline"
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Contrato
                      </a>
                    )}
                    {cliente.documento_comprovante_url && (
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${cliente.documento_comprovante_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-xs text-blue-400 hover:text-blue-300 hover:underline"
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Comprovante
                      </a>
                    )}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <div className="flex space-x-2">
                  {!isSubAdmin && (
                    <Button 
                      data-testid={`btn-editar-cliente-${cliente.id}`}
                      variant="outline" 
                      size="sm" 
                      className="flex-1 border-white/20 text-gray-300 hover:bg-white/10"
                      onClick={() => handleEdit(cliente)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Editar
                    </Button>
                  )}
                  
                  {!isSubAdmin && (cliente.bloqueado ? (
                    <Button 
                      data-testid={`btn-desbloquear-cliente-${cliente.id}`}
                      variant="outline" 
                      size="sm" 
                      className="flex-1 text-emerald-400 border-emerald-500/30 hover:text-emerald-300 hover:bg-emerald-500/10"
                      onClick={() => desbloquearCliente(cliente)}
                    >
                      ✅ Desbloquear
                    </Button>
                  ) : (
                    <Button 
                      data-testid={`btn-bloquear-cliente-${cliente.id}`}
                      variant="outline" 
                      size="sm" 
                      className="flex-1 text-orange-400 border-orange-500/30 hover:text-orange-300 hover:bg-orange-500/10"
                      onClick={() => abrirDialogoBloqueio(cliente)}
                    >
                      🚫 Bloquear
                    </Button>
                  ))}
                </div>

                {!isSubAdmin && (
                  <Button
                    data-testid={`btn-criar-acesso-app-${cliente.id}`}
                    variant="outline"
                    size="sm"
                    className="w-full text-emerald-400 border-emerald-500/30 hover:text-emerald-300 hover:bg-emerald-500/10"
                    onClick={() => criarAcessoApp(cliente)}
                  >
                    📲 {cliente.app_senha_hash ? 'Resetar senha + enviar WhatsApp' : 'Criar acesso + enviar WhatsApp'}
                  </Button>
                )}
                
                {!isSubAdmin && (
                  <Button 
                    data-testid={`btn-excluir-cliente-${cliente.id}`}
                    variant="outline" 
                    size="sm" 
                    className="w-full text-red-400 border-red-500/30 hover:text-red-300 hover:bg-red-500/10"
                    onClick={() => handleDelete(cliente)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {clientes.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-400 mb-2">
              Nenhum cliente cadastrado
            </h3>
            <p className="text-gray-400 mb-4">
              {isSubAdmin ? 'Aguardando cadastro pelo administrador' : 'Comece cadastrando seu primeiro cliente'}
            </p>
            {!isSubAdmin && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setShowModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Cadastrar Primeiro Cliente
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Diálogo de Cliente Duplicado */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              <span>Cliente Já Cadastrado</span>
            </DialogTitle>
          </DialogHeader>
          
          {clienteDuplicado && (
            <div className="space-y-4">
              <Alert className="border-orange-500/30 bg-orange-500/10">
                <AlertTriangle className="h-4 w-4 text-orange-400" />
                <div className="ml-2">
                  <p className="font-medium text-orange-300">
                    Já existe um cliente cadastrado com este CPF:
                  </p>
                  <div className="mt-2 p-3 bg-slate-800 rounded border border-white/10">
                    <p className="font-semibold text-white">{clienteDuplicado.nome}</p>
                    <p className="text-sm text-gray-300">CPF: {clienteDuplicado.cpf}</p>
                    <p className="text-sm text-gray-300">Tel: {clienteDuplicado.telefone}</p>
                  </div>
                </div>
              </Alert>
              
              <div className="flex space-x-2">
                <Button 
                  onClick={irParaClienteExistente}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Ver Cliente Existente
                </Button>
                <Button 
                  onClick={continuarCadastro}
                  variant="outline"
                  className="flex-1"
                >
                  Continuar Editando
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de Bloqueio */}
      <Dialog open={showBloqueioDialog} onOpenChange={setShowBloqueioDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-rose-400">
              <AlertTriangle className="h-5 w-5" />
              <span>Bloquear Cliente</span>
            </DialogTitle>
          </DialogHeader>
          
          {clienteSelecionado && (
            <div className="space-y-4">
              <Alert className="border-red-500/30 bg-red-500/10">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <div className="ml-2">
                  <p className="font-medium text-red-300">
                    Você está prestes a bloquear:
                  </p>
                  <div className="mt-2 p-3 bg-slate-800 rounded border border-white/10">
                    <p className="font-semibold text-white">{clienteSelecionado.nome}</p>
                    <p className="text-sm text-gray-300">CPF: {clienteSelecionado.cpf}</p>
                    <p className="text-sm text-gray-300">Tel: {clienteSelecionado.telefone}</p>
                  </div>
                </div>
              </Alert>
              
              <div>
                <Label htmlFor="motivo-bloqueio">Motivo do Bloqueio *</Label>
                <Input
                  id="motivo-bloqueio"
                  value={motivoBloqueio}
                  onChange={(e) => setMotivoBloqueio(e.target.value)}
                  placeholder="Ex: Inadimplência, Danos à moto, Comportamento inadequado..."
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="observacoes-bloqueio">Observações Adicionais</Label>
                <Input
                  id="observacoes-bloqueio"
                  value={observacoesBloqueio}
                  onChange={(e) => setObservacoesBloqueio(e.target.value)}
                  placeholder="Detalhes adicionais sobre o bloqueio..."
                  className="mt-1"
                />
              </div>
              
              <div className="flex space-x-2">
                <Button 
                  onClick={bloquearCliente}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={!motivoBloqueio.trim()}
                >
                  🚫 Confirmar Bloqueio
                </Button>
                <Button 
                  onClick={() => setShowBloqueioDialog(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};